'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Loader2Icon } from 'lucide-react';
const buttonVariants = cva("cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", {
  variants: {
    variant: {
      default: 'bg-primary text-primary-foreground hover:bg-primary/90',
      brand: 'bg-brand text-primary-foreground hover:bg-brand/90',
      'brand-darken': 'bg-brand-darken text-primary-foreground hover:bg-brand-darken/90',
      'brand-dark': 'bg-brand-dark text-primary-foreground hover:bg-brand-dark/90',
      success: 'bg-success text-white hover:bg-success/90',
      warning: 'bg-warning text-white hover:bg-warning/90',
      info: 'bg-info text-white hover:bg-info/90',
      error: 'bg-error text-white hover:bg-error/90',
      gray: 'bg-gray-500 text-white hover:bg-gray-600',
      destructive: 'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
      outline: 'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
      secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
      ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
      link: 'text-primary underline-offset-4 hover:underline'
    },
    size: {
      default: 'h-9 px-4 py-2 has-[>svg]:px-3',
      sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
      lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
      xl: 'h-12 rounded-md px-8 has-[>svg]:px-6',
      icon: 'size-9',
      'icon-sm': 'size-8',
      'icon-lg': 'size-10'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
function Button(t0) {
  const $ = _c(39);
  let children;
  let className;
  let disabled;
  let props;
  let size;
  let t1;
  let t2;
  let t3;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      asChild: t1,
      iconPosition: t2,
      loading: t3,
      children,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = props;
    $[5] = size;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    props = $[4];
    size = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    variant = $[9];
  }
  const asChild = t1 === undefined ? false : t1;
  const iconPosition = t2 === undefined ? "left" : t2;
  const loading = t3 === undefined ? false : t3;
  const Comp = asChild ? Slot : "button";
  let orderedChildren;
  let t4;
  if ($[10] !== asChild || $[11] !== children || $[12] !== className || $[13] !== disabled || $[14] !== iconPosition || $[15] !== loading || $[16] !== props || $[17] !== size || $[18] !== variant) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const childrenArray = React.Children.toArray(children);
      const isIconElement = _temp;
      const hasIcon = childrenArray.some(isIconElement);
      orderedChildren = hasIcon && iconPosition === "right" ? [...childrenArray.filter(child_0 => !isIconElement(child_0)), ...childrenArray.filter(child_1 => isIconElement(child_1))] : children;
      if (asChild) {
        const firstValidChild = React.Children.toArray(orderedChildren).find(_temp2) as React.ReactElement<{
          className?: string;
          disabled?: boolean;
          children?: React.ReactNode;
        }> | undefined;
        if (!firstValidChild) {
          throw new Error("Button with asChild prop must have at least one valid child element");
        }
        let t5;
        if ($[21] !== loading) {
          t5 = loading && <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />;
          $[21] = loading;
          $[22] = t5;
        } else {
          t5 = $[22];
        }
        let t6;
        if ($[23] !== firstValidChild || $[24] !== t5) {
          t6 = <>{t5}{firstValidChild.props.children}</>;
          $[23] = firstValidChild;
          $[24] = t5;
          $[25] = t6;
        } else {
          t6 = $[25];
        }
        t4 = React.cloneElement(firstValidChild, {
          ...props,
          className: cn(buttonVariants({
            variant,
            size,
            className
          }), firstValidChild.props.className),
          disabled: loading || disabled || firstValidChild.props.disabled,
          children: t6
        });
        break bb0;
      }
    }
    $[10] = asChild;
    $[11] = children;
    $[12] = className;
    $[13] = disabled;
    $[14] = iconPosition;
    $[15] = loading;
    $[16] = props;
    $[17] = size;
    $[18] = variant;
    $[19] = orderedChildren;
    $[20] = t4;
  } else {
    orderedChildren = $[19];
    t4 = $[20];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[26] !== className || $[27] !== size || $[28] !== variant) {
    t5 = cn(buttonVariants({
      variant,
      size,
      className
    }));
    $[26] = className;
    $[27] = size;
    $[28] = variant;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  const t6 = loading || disabled;
  let t7;
  if ($[30] !== loading) {
    t7 = loading && <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />;
    $[30] = loading;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== Comp || $[33] !== orderedChildren || $[34] !== props || $[35] !== t5 || $[36] !== t6 || $[37] !== t7) {
    t8 = <Comp data-slot="button" className={t5} disabled={t6} {...props}>{t7}{orderedChildren}</Comp>;
    $[32] = Comp;
    $[33] = orderedChildren;
    $[34] = props;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  return t8;
}
function _temp2(child_2) {
  return React.isValidElement(child_2);
}
function _temp(child) {
  if (!React.isValidElement(child)) {
    return false;
  }
  if ("type" in child && (child.type === "svg" || typeof child.type === "string" && child.type.includes("svg"))) {
    return true;
  }
  return "type" in child && typeof child.type === "function" && "props" in child && child.props !== null && typeof child.props === "object" && "className" in child.props && typeof child.props.className === "string" && (child.props.className.includes("h-") || child.props.className.includes("w-"));
}
export { Button, buttonVariants };